import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { Icon, InputGroup, InputNumber } from 'rsuite';
import InputGroupAddon from 'rsuite/lib/InputGroup/InputGroupAddon';
import styled from 'styled-components';
import { errorActions } from '../app/error-slice';
import * as cs from '../components/common/common-styles';
import { VerticalStepper } from '../components/common/vertical-stepper';
import { applyStrLicenseSteps } from '../flows/apply-str-licence/apply-str-license-steps';
import { LicenseWarnings } from '../parcel-lookup/license-warnings';
import { strRegistrationApiSender } from '../utils/str-registration-api-sender';
import { BackContinueButton } from '../widgets/back-continue-button';

const BusinessIdResultWrapper = styled.div`
  margin-top: 40px;

  .business-id-list-item {
    font-size: 22px;
    color: black;
  }
`;

const ErrorMessageWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  height: 47px;
  background-color: #fff6f6;
  padding-left: 30px;
  border: 1px solid red;
  border-radius: 4px;

  .error-list-item {
    font-size: 22px;
    color: red;
  }
`;

const BusinessIdDescription = styled.span`
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  color: #000000;
`;

const BusinessIdAddress = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #5c5d5d;
`;

const StyledInputNumber = styled(InputNumber)`
  border: ${(props) => (props.error ? '1px solid red !important' : 'inherit')};

  .rs-input {
    background-color: ${(props) => (props.error ? '#fff6f6' : 'inherit')};
    color: ${(props) => (props.error ? 'red' : 'inherit')};
  }

  .rs-input-number-btn-group-vertical {
    background-color: ${(props) => (props.error ? '#fff6f6' : 'inherit')};
  }

  .rs-icon-arrow-up-line,.rs-icon-arrow-down-line {
    display: none;
  }
`;

const StyledA = styled.a`
  font-family: Lato;
  font-size: 14px;
  line-height: 1.5;
  color: #027680;
  display: block;
  margin-top: 20px;
`;

function WarningTypeFactory({ businessIdPaid, foundBusiness }) {
  const tier = foundBusiness?.extras?.tier;

  if (businessIdPaid) return 'paidLicense';
  if (tier === '1' || tier === '') return 'quarterlyReporter';
  return null
}

/* eslint-disable no-unused-vars */
export function BusinessIdInput(props) {
  const { reset, setEnteredFlow, businessInfo, setBusinessInfo, setLicenseFeeInfo } = props;

  const dispatch = useDispatch();

  const [foundBusiness, setFoundBusiness] = useState(businessInfo);
  const [businessIdPaid, setBusinessIdPaid] = useState(false);
  const [lookupError, setLookupError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [businessIdInputValue, setBusinessIdInputValue] = useState(businessInfo?.licenseId);

  const history = useHistory();

  useEffect(() => {
    reset();
    setEnteredFlow(true);
  }, []);

  const setBusinessIdInputValueCB = useCallback((value) => {
    setBusinessIdInputValue(value);
    setFoundBusiness(null);
    setLookupError(false);
  }, [setBusinessIdInputValue, setFoundBusiness, setLookupError]);

  const findBusiness = useCallback(async () => {
    try {
      setLookupError(false);
      setIsLoading(true);

      const { data } = await strRegistrationApiSender.get(`/lookupLicense/${businessIdInputValue}`);

      setFoundBusiness(data);
      setIsLoading(false);
    } catch (err) {
      setLookupError(true);
      setIsLoading(false);
    }
  }, [businessIdInputValue, dispatch, setFoundBusiness, setIsLoading]);

  const calculateFeesCB = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await strRegistrationApiSender
        .get(`/calculateBusinessLicenseFee/${foundBusiness.licenseId}`);

      setIsLoading(false);
      setLicenseFeeInfo(data);
    } catch (err) {
      Sentry.captureException(err);
      setIsLoading(false);

      dispatch(errorActions.setHasErrored(Boolean(err)));
    }
  }, [history, setLicenseFeeInfo, foundBusiness]);

  const continueButtonCB = useCallback(async () => {
    if (foundBusiness) {
      setBusinessInfo(foundBusiness);
      if (foundBusiness.extras.tier !== 'TIER1') {
        await calculateFeesCB();
        history.push('/confirmation');
      } else {
        history.push('/revenue');
      }
    } else {
      await findBusiness();
    }
  }, [history, findBusiness, foundBusiness]);

  const clearCB = useCallback(() => {
    setLookupError(false);
    setBusinessIdInputValue('');
  }, [setLookupError, setBusinessIdInputValue]);

  const continueButtonLabel = useMemo(
    () => (foundBusiness || lookupError ? "Continue" : "Find Business"),
    [foundBusiness, lookupError]
  );

  const continueButtonDisabled = useMemo(
    () =>
      !foundBusiness &&
      lookupError &&
      isLoading &&
      businessIdPaid ||
      !businessIdInputValue,
    [foundBusiness, lookupError, isLoading, businessIdPaid, businessIdInputValue]
  );

  const warningType = WarningTypeFactory({ businessIdPaid, foundBusiness });

  return (
    <VerticalStepper activeStep={applyStrLicenseSteps['Property']}>
      <cs.Wrapper style={{ marginTop: 60 }}>
        <cs.HeaderText>Let’s get started by entering your Account Number (Business Tax ID):</cs.HeaderText>
        <InputGroup inside style={{ width: '200px' }}>
          <StyledInputNumber
            error={+lookupError}
            value={businessIdInputValue}
            onChange={setBusinessIdInputValueCB}
          />
          <InputGroupAddon
            style={{ right: 0 }}
            onClick={lookupError ? clearCB : null}
          >
            {foundBusiness && <Icon icon="check" />}
            {lookupError && <Icon style={{ color: 'red' }} icon="close" />}
          </InputGroupAddon>
        </InputGroup>

        <StyledA href="https://str-registration-public-bucket.s3.us-west-2.amazonaws.com/tbid_latest_account_list.xlsx" download>
          Help me find my Account Number
        </StyledA>

        {foundBusiness && (
          <BusinessIdResultWrapper>
            <li className="business-id-list-item">
              <BusinessIdDescription>
                <span>{foundBusiness.licenseId}</span>
                <span>&nbsp;-&nbsp;</span>
                <span>{foundBusiness.registrantName}</span>
              </BusinessIdDescription>
            </li>
            <BusinessIdAddress>{foundBusiness.propertyAddress}</BusinessIdAddress>
          </BusinessIdResultWrapper>
        )}

        {lookupError && (
          <ErrorMessageWrapper>
            <Icon icon="circle" style={{ fontSize: '8px', color: 'red' }} />
            <span style={{ color: 'red', marginLeft: 24 }}>
              Invalid Account Number
            </span>
          </ErrorMessageWrapper>
        )}

        {warningType && <LicenseWarnings warningType={warningType} />}

        <BackContinueButton
          backHidden={true}
          continueLabel={continueButtonLabel}
          onContinueClick={continueButtonCB}
          continueDisabled={continueButtonDisabled}
          isLoading={isLoading}
        />

        <cs.ImageWrapper>
          <img
            src="../../assets/apply-tot-property.svg"
            alt="Image of a house with trees"
          />
        </cs.ImageWrapper>
      </cs.Wrapper>
    </VerticalStepper>
  );
}

